import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getInstrument } from '@/lib/schema/loader';
import { useObservation } from '@/lib/state/useObservation';

/**
 * Left navigation rail: 6 subscales, each expandable to show its items.
 * Active item highlighted (matching wireframe's "Math/Number" state).
 */
export function SubscaleSidebar({ instrumentId }: { instrumentId: string }) {
  const instrument = getInstrument(instrumentId);
  const { itemId: activeItemId } = useParams<{ itemId: string }>();
  const { state } = useObservation();

  const activeSubscaleId = useMemo(() => {
    if (!instrument || !activeItemId) return instrument?.subscales[0]?.id;
    for (const s of instrument.subscales) {
      if (s.items.some((i) => i.id === activeItemId)) return s.id;
    }
    return instrument.subscales[0]?.id;
  }, [instrument, activeItemId]);

  const [expandedId, setExpandedId] = useState<string | undefined>(
    activeSubscaleId,
  );

  // When the observer navigates to another subscale via the top ribbon (or
  // any other route change), expand that subscale's items so the sidebar
  // stays in sync with "where am I". Users can still toggle other subscales
  // open by clicking; we only react to active-subscale changes.
  useEffect(() => {
    if (activeSubscaleId) setExpandedId(activeSubscaleId);
  }, [activeSubscaleId]);

  if (!instrument) return null;

  return (
    <nav
      aria-label="Subscales"
      className="flex h-full w-64 flex-shrink-0 flex-col gap-1 overflow-y-auto border-r bg-background py-3"
    >
      {instrument.subscales.map((subscale) => {
        const expanded = expandedId === subscale.id;
        const hasActive = subscale.items.some((i) => i.id === activeItemId);
        return (
          <div key={subscale.id}>
            <button
              type="button"
              onClick={() =>
                setExpandedId(expanded ? undefined : subscale.id)
              }
              className={cn(
                'flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-sm font-medium transition',
                (expanded || hasActive)
                  ? 'bg-accent text-dece-navy'
                  : 'text-foreground/80 hover:bg-accent/60',
              )}
              aria-expanded={expanded}
              aria-controls={`subscale-${subscale.id}-items`}
            >
              <span className="flex items-center gap-3">
                <span
                  className="inline-flex h-6 w-6 items-center justify-center rounded-sm bg-dece-navy/10 text-[11px] font-bold text-dece-navy"
                  aria-hidden
                >
                  S{subscale.number}
                </span>
                <span>{subscale.name}</span>
              </span>
              {expanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
            {expanded && (
              <ul
                id={`subscale-${subscale.id}-items`}
                className="flex flex-col border-l border-accent"
              >
                {subscale.items.map((item) => {
                  const active = item.id === activeItemId;
                  const status = state.itemStatus[item.id] ?? 'untouched';
                  return (
                    <li key={item.id}>
                      <Link
                        to={`/observation/${state.id}/items/${item.id}`}
                        className={cn(
                          'flex items-center gap-2 py-1.5 pl-12 pr-4 text-sm transition',
                          active
                            ? 'bg-primary/5 font-medium text-primary'
                            : 'text-foreground/70 hover:bg-accent/60',
                        )}
                        aria-current={active ? 'page' : undefined}
                      >
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {item.number}.
                        </span>
                        <span className="flex-1 truncate">{item.name}</span>
                        {status === 'complete' && (
                          <span
                            className="h-2 w-2 rounded-full bg-rating-5"
                            aria-label="Complete"
                          />
                        )}
                        {status === 'in_progress' && (
                          <span
                            className="h-2 w-2 rounded-full bg-rating-3"
                            aria-label="In progress"
                          />
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </nav>
  );
}
